// NodeNumberField.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './NodeNumberField.css';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * NodeNumberField
 *
 * Displays an inline number input for schema-driven fields with save-on-blur.
 * In readOnly / previewMode (visitor Peek): display-only value.
 *
 * @param {string} value - Current field value.
 * @param {Function} onChange - Callback when new number is set.
 * @param {boolean} [readOnly=false] - Display-only.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeNumberField = ({
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const isReadOnly = Boolean(readOnly || previewMode);

  // ----- STATE ----- //

  const [draftValue, setDraftValue] = useState(value ?? '');

  // ----- EFFECTS ----- //

  useEffect(() => {
    setDraftValue(value ?? '');
  }, [value]);

  // ----- HANDLERS ----- //

  const handleChange = useCallback((event) => {
    if (isReadOnly) return;
    setDraftValue(event?.target?.value ?? '');
  }, [isReadOnly]);

  const handleBlur = useCallback(() => {
    if (isReadOnly || typeof onChange !== 'function') return;
    const nextValue = draftValue ?? '';
    const currentValue = value ?? '';
    if (nextValue !== currentValue) {
      onChange(nextValue);
    }
  }, [draftValue, isReadOnly, onChange, value]);

  // ----- RENDER ----- //

  return (
    <>
      <div className="node-number-field-container">

        <TextInput
          type="number"
          value={draftValue}
          placeholder={isReadOnly ? '' : 'Set value...'}
          startIconKey="tag"
          startIconColor={theme.foregroundMuted}
          iconSize={22}
          hybrid
          readOnly={isReadOnly}
          onBlur={handleBlur}
          onChange={isReadOnly ? undefined : handleChange}
        />
      </div>
    </>
  );
};

NodeNumberField.displayName = 'NodeNumberField';

export default NodeNumberField;
